import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {proyectosApi} from '../../../api/api';
import { AdminPageHeader } from '../ui/AdminPageHeader';
import { FieldGrid, TextField, SelectField } from '../../../ui/form/fields';
import '../ui/adminUi.css';
import {writeProjectDraft} from "../projects/projectDraft";
import { useTranslation } from 'react-i18next';
import { slugify } from '../../shared/utils/slugify';
import { useNotifications } from '../../../ui/notifications';

const getEstadoOptions = (t: (key: string) => string) => [
    {value: 'En Ejecucion', label: t('informacionGeneralSection.statusOptions.En Ejecucion')},
    {value: 'Solicitado', label: t('informacionGeneralSection.statusOptions.Solicitado')},
    {value: 'En Valoracion', label: t('informacionGeneralSection.statusOptions.En Valoracion')},
    {value: 'Concedido', label: t('informacionGeneralSection.statusOptions.Concedido')},
    {value: 'Borrador', label: t('informacionGeneralSection.statusOptions.Borrador')}
];

export function AdminProjectCreatePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { showError } = useNotifications();
    const { register, handleSubmit, formState: { isValid, errors } } = useForm({
        mode: 'onChange',
        defaultValues: {
            denominacion: '',
            estado: 'Solicitado'
        }
    });

    const createM = useMutation({
        mutationFn: async (data: { denominacion: string; estado: string }) => {
            const created = await proyectosApi.create({
                informacionGeneral: {
                    denominacion: data.denominacion,
                    estado: data.estado
                }
            });
            if (!created?.id) throw new Error('No se pudo generar el ID del proyecto');
            const draftKey = `proyecto-${created.id}`;
            writeProjectDraft(draftKey, {
                informacionGeneral: {
                    denominacion: data.denominacion,
                    estado: data.estado
                },
                requerimientos: [],
                autorizacionesSolicitadas: [],
                datosContratos: { contratoMayores: [], contratoMenores: [], gastosImputadosFueraDeLaSubvencion: [] },
                datosContables: {},
                seguimientoPlataforma: { datosVariables: { columns: ["1"], rows: [[""]], cellStyles: {} } },
                seguimientoCoFFE: { datosVariables: { columns: ["1"], rows: [[""]], cellStyles: {} } },
                certificacionesObra: { datosVariables: { columns: ["1"], rows: [[""]], cellStyles: {} } },
                hitosYObjetivos: [],
                participacionCiudadana: [],
                comunicacionProyecto: [],
                plazosEjecucionJustificacion: [],
                reuniones: [],
                informesGestion: []
            }, true);

            return { ...created, slug: slugify(data.denominacion) };
        },
        onSuccess: (created) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'proyectos'] });
            navigate(`/administracion/proyectos/${created.slug}`, { replace: true });
        },
        onError: (error) => {
            showError(t('adminProjectCreate.createError'), error instanceof Error ? error.message : String(error));
        }
    });
    const ESTADO_OPTIONS = getEstadoOptions(t);
    return (
        <div className="adminPageWrap">
            <AdminPageHeader
                title={t('adminProjectCreate.newProject')}
                description={t('adminProjectCreate.fillFields')}
                actions={<Link className="btn btnSecondary" to="/administracion/proyectos">{t('adminProjectCreate.cancel')}</Link>}
            />

            <form onSubmit={handleSubmit((data) => createM.mutate(data))}>
                <section className="adminContentCard">
                    <FieldGrid>
                        <TextField
                            label={t('adminProjectCreate.denomination')}
                            name="denominacion"
                            register={register}
                            rules={{ required: t('adminProjectCreate.denominationRequired') }}
                            error={errors.denominacion}
                        />
                        <SelectField
                            label={t('adminProjectCreate.initialState')}
                            name="estado"
                            register={register}
                            options={ESTADO_OPTIONS}
                        />
                    </FieldGrid>
                    <div className="adminToolbar" style={{ marginTop: '2rem', justifyContent: 'flex-end' }}>
                        <button className="btn btnPrimary" type="submit" disabled={!isValid || createM.isPending}>
                            {createM.isPending ? t('adminProjectCreate.creating') : t('adminProjectCreate.confirmAndContinue')}
                        </button>
                    </div>
                </section>
            </form>
        </div>
    );
}
